/*
  <product-reels> — tira de reels de colaboraciones con visor a pantalla completa.
  Usado por sections/main-product-sc.liquid.

  Cada reel guarda su <video> dentro de un <template> (inerte: no descarga nada). El vídeo solo
  se clona en el visor al abrirlo, y se destruye al cerrar o al pasar a otro, para que nunca haya
  más de un vídeo descargándose o sonando a la vez.
*/
if (!customElements.get('product-reels')) {
  customElements.define(
    'product-reels',
    class ProductReels extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.product-reels__track');
        this.items = Array.from(this.querySelectorAll('.product-reels__item'));
        this.dialog = this.querySelector('.product-reels__dialog');
        this.stage = this.querySelector('.product-reels__stage');
        this.info = this.querySelector('.product-reels__info');
        this.progress = this.querySelector('.product-reels__progress span');
        this.soundButton = this.querySelector('.product-reels__sound');
        this.prevButton = this.querySelector('.product-reels__nav--prev');
        this.nextButton = this.querySelector('.product-reels__nav--next');
        this.scrollPrev = this.querySelector('.product-reels__scroll--prev');
        this.scrollNext = this.querySelector('.product-reels__scroll--next');

        this.current = -1;
        // Se empieza en silencio porque los navegadores bloquean el autoplay con sonido;
        // la elección del usuario se mantiene al pasar de un reel a otro.
        this.muted = true;

        if (!this.track || !this.dialog) return;

        this.items.forEach((item, index) => {
          item.querySelector('.product-reels__card')?.addEventListener('click', () => this.open(index));
        });

        this.querySelector('.product-reels__close')?.addEventListener('click', () => this.close());
        this.prevButton?.addEventListener('click', () => this.show(this.current - 1));
        this.nextButton?.addEventListener('click', () => this.show(this.current + 1));
        this.soundButton?.addEventListener('click', () => this.toggleSound());

        // Click en el fondo (fuera del visor) cierra.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        // La limpieza no se cuelga solo del evento 'close' del <dialog>: se despacha de forma
        // asíncrona y en la verificación hubo casos en que no llegó, dejando el vídeo sonando y el
        // scroll bloqueado. Se limpia de forma explícita en close(), y 'cancel' (Esc) es síncrono.
        this.dialog.addEventListener('cancel', () => this.teardown());
        this.dialog.addEventListener('close', () => this.teardown());

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.show(this.current + 1);
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.show(this.current - 1);
          }
        });

        this.initScrollButtons();
      }

      initScrollButtons() {
        if (!this.scrollPrev || !this.scrollNext) return;

        const update = () => {
          const max = this.track.scrollWidth - this.track.clientWidth;
          this.scrollPrev.hidden = this.track.scrollLeft <= 2;
          this.scrollNext.hidden = this.track.scrollLeft >= max - 2;
        };

        const step = () => this.track.clientWidth * 0.8;
        this.scrollPrev.addEventListener('click', () => this.track.scrollBy({ left: -step() }));
        this.scrollNext.addEventListener('click', () => this.track.scrollBy({ left: step() }));
        this.track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }

      open(index) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        if (!this.dialog.open) {
          this.dialog.showModal();
          document.body.classList.add('overflow-hidden');
        }
        this.show(index);
      }

      close() {
        if (this.dialog.open) this.dialog.close();
        this.teardown();
      }

      show(index) {
        if (index < 0 || index >= this.items.length) return;

        this.clearVideo();
        this.current = index;

        const item = this.items[index];
        const template = item.querySelector('template.product-reels__source');
        const video = template?.content.querySelector('video')?.cloneNode(true);

        if (video) {
          video.muted = this.muted;
          video.playsInline = true;
          video.addEventListener('timeupdate', () => {
            if (this.progress && video.duration) {
              this.progress.style.width = `${(video.currentTime / video.duration) * 100}%`;
            }
          });
          this.stage.appendChild(video);
          this.video = video;
          video.play().catch(() => {
            // Si el navegador rechaza el autoplay (p. ej. con sonido), se reintenta en silencio.
            video.muted = true;
            this.setMuted(true);
            video.play().catch(() => {});
          });
        }

        const meta = item.querySelector('.product-reels__meta');
        this.info.innerHTML = meta ? meta.innerHTML : '';

        this.prevButton.disabled = index === 0;
        this.nextButton.disabled = index === this.items.length - 1;
        this.setMuted(this.muted);
      }

      toggleSound() {
        this.setMuted(!this.muted);
        if (this.video) {
          this.video.muted = this.muted;
          if (!this.muted) this.video.play().catch(() => {});
        }
      }

      setMuted(muted) {
        this.muted = muted;
        if (!this.soundButton) return;
        this.soundButton.setAttribute('aria-pressed', String(!muted));
        this.soundButton.setAttribute(
          'aria-label',
          muted ? this.soundButton.dataset.labelOn : this.soundButton.dataset.labelOff
        );
      }

      clearVideo() {
        if (this.video) {
          this.video.pause();
          // Vaciar el src corta la descarga en curso; si no, el navegador sigue bajando el archivo.
          this.video.removeAttribute('src');
          this.video.querySelectorAll('source').forEach((source) => source.remove());
          this.video.load();
          this.video = null;
        }
        this.stage.innerHTML = '';
        if (this.progress) this.progress.style.width = '0';
      }

      teardown() {
        this.clearVideo();
        this.current = -1;
        document.body.classList.remove('overflow-hidden');
      }
    }
  );
}
